import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { OrbitProgress } from "react-loading-indicators";
import { Store, MapPin, Calendar, RefreshCw } from "lucide-react";

import { useLatestShopsData } from "../../../services/API/ShopAPI/GetLatestShops.jsx";
import ShopDetails from "./ShopDetails.jsx";
import { useApiPopup } from "../ApiPopup/ApiPopupProvider.jsx";

// -----------------------------------------------------------------------------
// Helper functions
// -----------------------------------------------------------------------------

/**
 * Return the most relevant timestamp available for a shop.
 *
 * The function supports different timestamp fields returned by the API.
 */
const pickTimestampMs = (shop) => {
  const value =
    shop?.last_activation ??
    shop?.last_updated ??
    shop?.date_created ??
    (shop?._ts ? shop._ts * 1000 : null);

  const timestamp = Number(value);

  return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Format a timestamp using the user's browser locale.
 */
const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return "-";
  }

  try {
    return new Date(timestamp).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

/**
 * Default shop avatar.
 */
const ShopIcon = () => (
  <div
    className="
      flex-shrink-0
      flex
      h-10
      w-10
      items-center
      justify-center
      rounded-full
      border
      border-stone-200
      bg-stone-100
      text-stone-600
    "
  >
    <Store size={18} />
  </div>
);

/**
 * Display a single shop row.
 *
 * This component does not manage modal state. It only reports the selected
 * shop to its parent through the onClick callback.
 */
const ShopRow = ({ shop, isLast, onClick }) => {
  const timestamp = pickTimestampMs(shop);

  const location = [shop?.city, shop?.country]
    .filter(Boolean)
    .join(", ");

  /**
   * Notify the parent that this shop was selected.
   */
  const handleClick = () => {
    onClick(shop);
  };

  return (
    <button
      type="button"
      // onClick={handleClick}
      className={`
        group
        flex
        w-full
        
        items-center
        gap-4
        px-4
        py-3
        text-left
        transition-colors
        duration-150
        hover:bg-stone-50
        focus:outline-none
        focus-visible:ring-2
        focus-visible:ring-inset
        focus-visible:ring-stone-400
        ${!isLast ? "border-b border-stone-100" : ""}
      `}
    >
      {/* Shop avatar */}
      <ShopIcon />

      {/* Main shop information */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h4
            className="
              truncate
              text-sm
              font-semibold
              text-stone-800
              transition-colors
              group-hover:text-stone-900
            "
          >
            {shop?.name ?? "Unknown Shop"}
          </h4>

          {shop?.is_shop_test && (
            <span
              className="
                rounded
                border
                border-yellow-200
                bg-yellow-50
                px-1.5
                py-0.5
                text-[10px]
                font-medium
                text-yellow-700
              "
            >
              Test
            </span>
          )}
        </div>

        <div className="mt-0.5 flex items-center gap-3 text-xs text-stone-500">
          <span
            className="
              max-w-[120px]
              truncate
              font-medium
              text-stone-600
              group-hover:text-stone-700
            "
          >
            {shop?.brand ?? "No Brand"}
          </span>

          {location && (
            <span className="flex min-w-0 items-center gap-1 text-stone-400">
              <span className="h-1 w-1 flex-shrink-0 rounded-full bg-stone-300" />

              <MapPin size={10} className="flex-shrink-0" />

              <span className="truncate">{location}</span>
            </span>
          )}
        </div>
      </div>

      {/* Last update timestamp */}
      <div className="hidden flex-shrink-0 flex-col items-end gap-1 sm:flex">
        <div
          className="
            flex
            items-center
            gap-1.5
            rounded-full
            border
            border-stone-200
            bg-stone-50
            px-3
            py-1
            text-xs
            font-medium
            text-stone-600
            shadow-sm
            transition-all
            group-hover:border-stone-300
            group-hover:bg-white
          "
        >
          <Calendar size={11} className="text-stone-400" />

          {formatTimestamp(timestamp)}
        </div>
      </div>
    </button>
  );
};

/**
 * Loading state displayed while the shop request is running.
 */
const LoadingState = () => (
  <div
    className="
      flex
      h-[400px]
      items-center
      justify-center
      rounded-2xl
      border
      border-stone-100
      bg-stone-50
      p-4
      shadow-sm
    "
  >
    <OrbitProgress
      color="#57534e"
      size="medium"
      text=""
      textColor=""
    />
  </div>
);

/**
 * Error state displayed when the shop request fails.
 */
const ErrorState = ({ message, onRetry }) => (
  <div
    className="
      flex
      h-[400px]
      flex-col
      items-center
      justify-center
      rounded-2xl
      border
      border-red-100
      bg-red-50
      p-6
      text-center
      shadow-sm
    "
  >
    <Store size={28} className="mb-3 text-red-400" />

    <h3 className="text-sm font-semibold text-red-900">
      Failed to load shops
    </h3>

    <p className="mt-1 max-w-md text-sm text-red-700">
      {message}
    </p>

    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="
          mt-4
          flex
          items-center
          gap-2
          rounded-lg
          border
          border-red-200
          bg-white
          px-3
          py-2
          text-sm
          font-medium
          text-red-700
          transition-colors
          hover:bg-red-100
          focus:outline-none
          focus-visible:ring-2
          focus-visible:ring-red-400
        "
      >
        <RefreshCw size={14} />
        Try again
      </button>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const ShopColumn = ({ title = "Latest Shops" }) => {
  const popup = useApiPopup();

  // Store the currently selected shop.
  const [selectedShop, setSelectedShop] = useState(null);

  // Control whether the details modal is open.
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Store the modal cleanup timer so it can be cancelled safely.
  const closeTimerRef = useRef(null);

  const {
    data: allShopsData = [],
    isPending,
    error,
    refetch,
  } = useLatestShopsData();

  /**
   * Sort shops only when the API data changes.
   *
   * useMemo avoids recreating and sorting the array on every render caused by
   * unrelated state updates, such as opening or closing the details modal.
   */
  const rows = useMemo(() => {
    const shops = Array.isArray(allShopsData) ? allShopsData : [];

    return [...shops].sort((shopA, shopB) => {
      const dateA = pickTimestampMs(shopA) ?? 0;
      const dateB = pickTimestampMs(shopB) ?? 0;

      return dateB - dateA;
    });
  }, [allShopsData]);

  /**
   * Display API errors after React finishes rendering.
   *
   * Calling popup.error directly inside the component body would update the
   * ApiPopupProvider while ShopColumn is rendering, which causes this warning:
   *
   * "Cannot update a component while rendering a different component."
   */
  useEffect(() => {
    if (!error) {
      return;
    }

    const message =
      error?.message || "Failed to load shops data.";

    popup.error(message);
  }, [error, popup]);

  /**
   * Cancel the close timer if the component unmounts.
   */
  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  /**
   * Open the shop details modal.
   */
  const handleShopClick = useCallback((shop) => {
    // Cancel a previous close timer when the user quickly opens another shop.
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setSelectedShop(shop);
    setIsDetailsOpen(true);
  }, []);

  /**
   * Close the shop details modal.
   *
   * The selected shop is cleared after the closing animation completes.
   */
  const closeDetails = useCallback(() => {
    setIsDetailsOpen(false);

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = setTimeout(() => {
      setSelectedShop(null);
      closeTimerRef.current = null;
    }, 300);
  }, []);

  /**
   * Retry the shop request manually.
   */
  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  // Display the loading state.
  if (isPending) {
    return <LoadingState />;
  }

  // Display an error state instead of rendering an empty successful result.
  if (error) {
    return (
      <ErrorState
        message={error?.message || "Failed to load shops data."}
        onRetry={handleRetry}
      />
    );
  }

  return (
    <div
      className="
        relative
        flex
        h-full
        max-h-[690px]
        flex-col
        overflow-auto
        rounded-2xl
        border
        border-stone-100
        bg-stone-50
        p-4
        shadow-sm
      "
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between px-1">
        <div>
          <h3 className="text-lg font-semibold text-stone-800">
            {title}
          </h3>

          <p className="text-sm text-stone-500">
            {rows.length} {rows.length === 1 ? "record" : "records"} — most
            recent shops.
          </p>
        </div>
      </div>

      {/* Shop list */}
      <div
        className="
          flex-1
          overflow-hidden
          rounded-xl
          border
          border-stone-200
          bg-white
          shadow-sm
        "
      >
        {rows.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-stone-400">
            <Store size={24} className="mb-2 opacity-50" />
            <span className="text-sm">No shops found</span>
          </div>
        ) : (
          <div className="custom-scrollbar h-full overflow-y-auto">
            {rows.map((shop, index) => (
              <ShopRow
                key={shop?.id ?? shop?._id ?? index}
                shop={shop}
                isLast={index === rows.length - 1}
                onClick={handleShopClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Only one modal instance is rendered for the selected shop */}
      {/* <ShopDetails
        isOpen={isDetailsOpen}
        shop={selectedShop}
        onClose={closeDetails}
      /> */}
    </div>
  );
};

export default ShopColumn;